import z from 'zod';

import { createElement } from '@@plate/serialization/creators/createElement';
import { getElementTypes } from '@@plate/utils/getElementTypes';

import { ParagraphCodec, UnityParagraph } from './Paragraph';
import { PlateTextElements } from './Text';
import { CURRENT_VERSION, ElementId, UnityElementId, Version } from '../commons';
import { ELEMENT_TYPES } from '../constants';

export const UnityInterviewSegment = z.object({
    type: z.literal(ELEMENT_TYPES.INTERVIEW_SEGMENT),
    elementId: UnityElementId,
    templateElement: z.boolean().optional(),
    question: UnityParagraph.nullable(),
    answers: z.array(UnityParagraph).nullable(),
    version: Version,
});
export type UnityInterviewSegment = z.infer<typeof UnityInterviewSegment>;

export const PlateInterviewSegmentQuestion = z.object({
    type: z.literal(ELEMENT_TYPES.INTERVIEW_SEGMENT_QUESTION),
    id: ElementId,
    data: z
        .object({
            templateElement: z.boolean().optional(),
        })
        .optional(),
    children: PlateTextElements,
});
export type InterviewSegmentQuestionElement = z.infer<typeof PlateInterviewSegmentQuestion>;

export const PlateInterviewSegmentAnswer = z.object({
    type: z.literal(ELEMENT_TYPES.INTERVIEW_SEGMENT_ANSWER),
    id: ElementId,
    data: z
        .object({
            templateElement: z.boolean().optional(),
        })
        .optional(),
    children: PlateTextElements,
});
export type InterviewSegmentAnswerElement = z.infer<typeof PlateInterviewSegmentAnswer>;

export const PlateInterviewSegment = z.object({
    type: z.literal(ELEMENT_TYPES.INTERVIEW_SEGMENT),
    id: ElementId,
    data: z
        .object({
            templateElement: z.boolean().optional(),
        })
        .optional(),
    children: z.tuple([PlateInterviewSegmentQuestion], PlateInterviewSegmentAnswer),
});
export type InterviewSegmentElement = z.infer<typeof PlateInterviewSegment>;

export const INTERVIEW_SEGMENT_ELEMENT_TYPES = getElementTypes([PlateInterviewSegment]);

export const InterviewSegmentCodec = z.codec(UnityInterviewSegment, PlateInterviewSegment, {
    decode: (value) => {
        const question: InterviewSegmentQuestionElement = value.question
            ? {
                  ...ParagraphCodec.decode(value.question),
                  type: ELEMENT_TYPES.INTERVIEW_SEGMENT_QUESTION,
              }
            : createElement(ELEMENT_TYPES.INTERVIEW_SEGMENT_QUESTION, {
                  children: [{ text: '' }],
              });

        const answers: InterviewSegmentAnswerElement[] = value.answers
            ? value.answers.map((answer) => ({
                  ...ParagraphCodec.decode(answer),
                  type: ELEMENT_TYPES.INTERVIEW_SEGMENT_ANSWER,
              }))
            : [];

        return {
            type: value.type,
            id: value.elementId ?? crypto.randomUUID(),
            ...(value.templateElement ? { data: { templateElement: value.templateElement } } : {}),
            children: [question, ...answers] satisfies [
                InterviewSegmentQuestionElement,
                ...InterviewSegmentAnswerElement[],
            ],
        };
    },
    encode: (value) => ({
        type: value.type,
        elementId: value.id,
        version: CURRENT_VERSION,
        question: ParagraphCodec.encode({
            ...value.children[0],
            type: 'paragraph',
        }),
        answers: value.children.slice(1).map((answer) =>
            ParagraphCodec.encode({
                ...answer,
                type: 'paragraph',
            }),
        ),
        ...value.data,
    }),
});
